import { foldEffect } from "@codemirror/language";
import type { Compartment } from "@codemirror/state";
import type { CodeEditorSettings } from "@whilecat/core/editors/code-editor-fabric.js";
import { requireNotNull } from "@whilecat/core/utils/validation.js";
import { EditorView } from "codemirror";

export type CheckBoxSelectedCallBack = () => void;

type CheckBoxOptions = {
    processor: CheckBoxSelectedCallBack;
    selected: Array<number>;
    disabled: boolean;
};

export class CodeEditorView {
    editor: EditorView;
    private editableCompartment: Compartment;
    private settings: CodeEditorSettings;
    private checkBoxOptions: CheckBoxOptions;

    public constructor(editor: EditorView, settings: CodeEditorSettings, editableCompartment: Compartment) {
        this.editor = requireNotNull(editor);
        this.settings = requireNotNull(settings);
        this.editableCompartment = requireNotNull(editableCompartment);
        this.checkBoxOptions = {
            processor: () => this.onCheckBoxSelect(),
            selected: new Array<number>(),
            disabled: false,
        };

        // FIXME
        // @ts-ignore
        editor.customOptions = this.checkBoxOptions;
    }

    public getCurrentCode() {
        return this.editor.state.doc.toString();
    }

    public setCode(code: string) {
        this.settings.code = code;
        if (this.editor) {
            this.editor.dispatch({
                changes: {
                    from: 0,
                    to: this.editor.state.doc.length,
                    insert: code,
                },
            });
        }
    }

    public setEditable(editable: boolean) {
        this.editor.dispatch({
            effects: this.editableCompartment.reconfigure(EditorView.editable.of(editable)),
        });
    }

    // FIXME: not really a good solution repasting current code
    public requestUpdate() {
        this.editor.dispatch({
            changes: {
                from: 0,
                to: this.editor.state.doc.length,
                insert: this.settings.code,
            },
        });
    }

    public getLineBy(lineNumber: number) {
        requireNotNull(this.editor);
        requireNotNull(lineNumber);

        return this.editor.state.doc.line(lineNumber);
    }

    public foldCode(startAt: number, endAt: number) {
        requireNotNull(startAt);
        requireNotNull(endAt);

        this.editor.dispatch({
            effects: foldEffect.of({
                from: startAt,
                to: endAt,
            }),
        });
    }

    onCheckBoxSelect() {
        this.checkBoxOptions.selected = this.getMarkedLines();
    }

    getMarkedLines() {
        return this.checkBoxOptions.selected;
    }

    markLines(lines: Array<number>) {
        if (!this.settings!.withCheckboxes) {
            return;
        }
        this.checkBoxOptions.selected = [...lines];
        this.requestUpdate();
    }

    disable() {
        this.settings.disabled = true;
        this.checkBoxOptions.disabled = true;
        this.requestUpdate();
    }

    focus() {
        this.editor.focus();
    }

    blur() {
        this.editor.contentDOM.blur();
    }
}
